import {StaffIdentity} from '../ui/AppLayout';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useParams } from 'react-router';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { formatDateTime } from '../dates';
import { addDaysDateOnly, todayUtcDateOnly, vatFromNet } from '../lib/form-defaults';

type Opportunity = {
  title: string;
  scope_snapshot: string;
  response_deadline: string | null;
  currency: string;
  contractor_name: string;
  status: string;
  sealed: boolean;
};

export function ProcurementResponse() {
  const { token } = useParams();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/external/procurement-response/${encodeURIComponent(token)}`)
      .then(async (response) => {
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.message ?? 'This invitation is not available.');
        return response.json();
      })
      .then(setOpportunity)
      .catch((reason) => setError(reason instanceof Error ? reason.message : String(reason)));
  }, [token]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setBusy(true);
    setError('');
    const data = new FormData(event.currentTarget);
    const payload = {
      providerReference: String(data.get('providerReference') ?? ''),
      priceNet: Number(data.get('priceNet')),
      taxAmount: Number(data.get('taxAmount') ?? 0),
      priceBreakdown: [],
      vatTreatment: String(data.get('vatTreatment') ?? ''),
      programme: String(data.get('programme') ?? ''),
      proposedStartDate: String(data.get('proposedStartDate') ?? '') || undefined,
      duration: String(data.get('duration') ?? ''),
      assumptions: String(data.get('assumptions') ?? '') || undefined,
      exclusions: String(data.get('exclusions') ?? '') || undefined,
      warranty: String(data.get('warranty') ?? '') || undefined,
      qualifications: String(data.get('qualifications') ?? '') || undefined,
      capacity: String(data.get('capacity') ?? '') || undefined,
      quality: String(data.get('quality') ?? '') || undefined,
      validityUntil: String(data.get('validityUntil') ?? ''),
      declarationName: String(data.get('declarationName') ?? ''),
      declarationAccepted: data.get('declarationAccepted') === 'on',
    };
    try {
      const response = await fetch(`/api/external/procurement-response/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error((await response.json().catch(() => null))?.message ?? 'Your bid could not be submitted.');
      setSent(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  if (error && !opportunity) return <><header className="provider-response-masthead"><StaffIdentity onDark/></header>
<main className="provider-bid-page"><section className="provider-bid-card"><h1>Invitation unavailable</h1><p>{error}</p></section></main></>;
  if (!opportunity) return <><header className="provider-response-masthead"><StaffIdentity onDark/></header>
<main className="provider-bid-page"><section className="provider-bid-card"><p>Loading your invitation…</p></section></main></>;
  if (sent || opportunity.status === 'Submitted') return <><header className="provider-response-masthead"><StaffIdentity onDark/></header>
<main className="provider-bid-page"><section className="provider-bid-card provider-bid-confirmation"><span>Bid received</span>
<h1>Thank you, {opportunity.contractor_name}</h1><p>Your sealed bid has been recorded. MYFM will contact you after the submission period and review process.</p></section></main></>;

  return <><header className="provider-response-masthead"><StaffIdentity onDark/></header>
<main className="provider-bid-page"><section className="provider-bid-card">
    <header><small>BlockGenie · Supplier response</small><h1>{opportunity.title}</h1><p>Invitation for <strong>{opportunity.contractor_name}</strong></p></header>
    <div className="provider-opportunity"><div><strong>Scope</strong><p>{opportunity.scope_snapshot}</p></div><div><strong>Closing date</strong><p>{formatDateTime(opportunity.response_deadline,'See invitation')}</p></div></div>
    <aside><strong>Sealed bidding</strong><p>Your bid is private. Contractors cannot see who else has been invited or the prices submitted. An invitation is not an award or instruction to begin work.</p></aside>
    <p className="form-help">This response link accepts the details below. Provide supporting files through the secure channel specified in your invitation.</p>
    <form onSubmit={submit} className="provider-bid-form">
      <label>Quote reference<input name="providerReference" minLength={6} required /></label>
      <BidPricing currency={opportunity.currency} />
      <label className="span-all">Programme and method<textarea name="programme" rows={3} required /></label>
      <label>Proposed start date<DateOnlyInput name="proposedStartDate" /></label>
      <label>Expected duration<input name="duration" placeholder="For example, 5 working days" required /></label>
      <label>Bid valid until<DateOnlyInput name="validityUntil" defaultValue={addDaysDateOnly(todayUtcDateOnly(), 30)} required /></label>
      <label className="span-all">Exclusions<textarea name="exclusions" rows={3} /></label>
      <details className="provider-bid-optional span-all"><summary>Additional assumptions and supplier details</summary><div className="provider-bid-form"><label className="span-all">Assumptions<textarea name="assumptions" rows={3} /></label>

      <label>Warranty offered<input name="warranty" /></label><label>Relevant qualifications<input name="qualifications" /></label>
      <label>Current capacity<input name="capacity" /></label><label>Quality proposal<input name="quality" /></label>
      </div></details>
      <fieldset className="span-all"><legend>Declaration</legend><label>Name of authorised declarant<input name="declarationName" required /></label><label className="provider-declaration"><input name="declarationAccepted" type="checkbox" required /> I confirm this bid is accurate, authorised and can be delivered as stated.</label></fieldset>
      {error && <p className="error span-all">{error}</p>}
      <button className="primary span-all" disabled={busy}>{busy ? 'Submitting sealed bid…' : 'Submit sealed bid'}</button>
    </form>
  </section></main></>;
}

/** F-090: VAT is worked out from a rate choice (the amount can still be overtyped); the bid stays valid for 30 days by default. */
const vatChoices=[{label:'VAT at 20% (standard rate)',rate:20},{label:'VAT at 5% (reduced rate)',rate:5},{label:'Zero-rated',rate:0},{label:'Exempt from VAT',rate:0}] as const;
function BidPricing({ currency }: { currency: string }) {
  const [net, setNet] = useState(''), [treatment, setTreatment] = useState<string>(vatChoices[0].label), [tax, setTax] = useState('0.00'), [taxOwn, setTaxOwn] = useState(false);
  const rate = (label: string) => vatChoices.find((choice) => choice.label === label)?.rate ?? 0;
  return <>
    <label>Net price ({currency})<input name="priceNet" type="number" min="0" step="0.01" required value={net} onChange={(event) => { setNet(event.target.value); if (!taxOwn) setTax(vatFromNet(event.target.value, rate(treatment)) || '0.00'); }} /></label>
    <label>VAT treatment<select name="vatTreatment" value={treatment} onChange={(event) => { setTreatment(event.target.value); setTaxOwn(false); setTax(vatFromNet(net, rate(event.target.value)) || '0.00'); }}>{vatChoices.map((choice) => <option key={choice.label}>{choice.label}</option>)}</select></label>
    <label>VAT / tax amount<input name="taxAmount" type="number" min="0" step="0.01" required value={tax} onChange={(event) => { setTaxOwn(true); setTax(event.target.value); }} /></label>
  </>;
}
